'use client'

import React, { useRef } from 'react'
import { DEFAULT_INTERSECTION_OPTIONS, useScrollIntersection } from './utils/windowScroll'

interface RevealOptions {
  id?: string
  className?: string
  children?: any
  intersectOptions?: any
  once?: boolean
  delay?: number
  duration?: number
  translateX?: number
  translateY?: number
  scale?: number
  opacity?: number
  func?: string
  style?: any
  onClick?: any
}

const DEFAULT_PROPS: RevealOptions = {
  id: '',
  className: '',
  intersectOptions: DEFAULT_INTERSECTION_OPTIONS,
  once: false,
  delay: 0,
  duration: 500,
  translateX: 0,
  translateY: 0,
  scale: 1,
  opacity: 0,
  func: 'ease-in-out',
  style: {},
  onClick: null
}

/**
 * Reveal Wrapper Component
 * ---------------------------------------------------------------------------------------------------------------------
 */
export default function Reveal(rawProps: RevealOptions) {
  const props = { ...DEFAULT_PROPS, ...rawProps }

  const ref = useRef<any>()
  const { show } = useScrollIntersection(ref, props.intersectOptions)

  const [isClientMounted, setIsClientMounted] = React.useState(false)
  const [isAnimatedVisible, setIsAnimatedVisible] = React.useState(false)

  React.useEffect(() => {
    setIsClientMounted(true)
  }, [])

  React.useEffect(() => {
    if (!isClientMounted) return // Animation logic only applies client-side post-mount

    if (show) {
      setIsAnimatedVisible(true)
    } else if (!props.once) {
      // If not 'once', element can be hidden again when out of view
      setIsAnimatedVisible(false)
    }
    // If 'props.once' is true and element has been shown, it remains visible
  }, [show, props.once, isClientMounted])

  const currentStyle: React.CSSProperties = { ...props.style }
  let currentClassName = props.className ?? ''

  if (isClientMounted) {
    // Client-side, after mount: apply animation styles
    currentClassName = `${currentClassName} transition-all`.trim()
    currentStyle.transform = isAnimatedVisible
      ? 'none'
      : `scale(${props.scale}) translateX(${props.translateX}px) translateY(${props.translateY}px)`
    currentStyle.opacity = isAnimatedVisible ? 1 : props.opacity
    currentStyle.transitionDelay = `${props.delay}ms`
    currentStyle.transitionDuration = `${props.duration}ms`
    currentStyle.transitionTimingFunction = props.func
  } else {
    // SSR or client-side before full JS hydration: content is visible for SEO and no-JS users
    currentStyle.opacity = 1
    currentStyle.transform = 'none'
    // No 'transition-all' class here to prevent style mismatch or FOUC
  }

  const finalClassName = currentClassName.trim() || undefined // Use undefined if empty for cleaner HTML

  return (
    <div
      ref={ref}
      id={props.id || undefined} // Render id attribute only if props.id has a value
      className={finalClassName}
      style={currentStyle}
      onClick={props.onClick}
    >
      {props.children}
    </div>
  )
}
